/**
 * SSE Record/Playback Engine (AIE-295)
 *
 * Captures SSE events passing through SSEConnectionManager.processEvent()
 * and replays them deterministically at configurable speed.
 *
 * Record: registers a listener on the manager, captures events with timestamps.
 * Playback: disconnects live SSE, feeds recorded events through processEvent().
 */

import {
  isHeartbeatEvent,
  SSEConnectionManager,
  type SSEEvent,
} from "@/hooks/useModernSSE";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlaybackSpeed = 1 | 2 | 4 | 8 | "max";
export type RecorderState = "idle" | "recording" | "playing" | "paused";

export interface SSERecording {
  version: 1;
  metadata: {
    recorded_at: string;
    total_events: number;
    total_duration_ms: number;
    study_ids: string[];
  };
  events: Array<{
    event: SSEEvent;
    delta_ms: number;
  }>;
}

interface RecorderOptions {
  onStateChange?: (state: RecorderState) => void;
  onPlaybackProgress?: (current: number, total: number) => void;
  onEventRecorded?: (event: SSEEvent, count: number) => void;
  onEventPlayed?: (event: SSEEvent) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getManager(): SSEConnectionManager {
  return SSEConnectionManager.getInstance();
}

// ---------------------------------------------------------------------------
// Class
// ---------------------------------------------------------------------------

export class SSERecorderPlayback {
  private state: RecorderState = "idle";
  private recordedEvents: Array<{ event: SSEEvent; timestamp: number }> = [];
  private recordingListener: ((event: SSEEvent) => void) | null = null;

  private currentRecording: SSERecording | null = null;
  private playbackIndex = 0;
  private playbackSpeed: PlaybackSpeed = 1;
  private currentTimer: number | null = null;
  private savedLastEventId: string | null = null;

  private opts: RecorderOptions;

  constructor(options: RecorderOptions = {}) {
    this.opts = options;
  }

  // -----------------------------------------------------------------------
  // State
  // -----------------------------------------------------------------------

  getState(): RecorderState {
    return this.state;
  }

  getRecordedEventCount(): number {
    return this.recordedEvents.length;
  }

  getPlaybackProgress(): { current: number; total: number } {
    return {
      current: this.playbackIndex,
      total: this.currentRecording?.events.length ?? 0,
    };
  }

  getCurrentRecording(): SSERecording | null {
    return this.currentRecording;
  }

  // -----------------------------------------------------------------------
  // Recording
  // -----------------------------------------------------------------------

  startRecording(): void {
    if (this.state === "playing" || this.state === "paused") {
      this.stopPlayback();
    }

    this.recordedEvents = [];

    const manager = getManager();

    this.recordingListener = (event: SSEEvent) => {
      // Skip heartbeats – they carry no useful debugging data
      if (isHeartbeatEvent(event)) return;

      this.recordedEvents.push({
        event,
        timestamp: performance.now(),
      });

      this.opts.onEventRecorded?.(event, this.recordedEvents.length);
    };

    manager.addListener(this.recordingListener);
    this.setState("recording");
  }

  stopRecording(): SSERecording {
    if (this.recordingListener) {
      const manager = getManager();
      manager.removeListener(this.recordingListener);
      this.recordingListener = null;
    }

    const recording = this.buildRecording();
    this.currentRecording = recording;
    this.setState("idle");
    return recording;
  }

  private buildRecording(): SSERecording {
    const studyIds = new Set<string>();
    const events: SSERecording["events"] = [];

    for (let i = 0; i < this.recordedEvents.length; i++) {
      const entry = this.recordedEvents[i]!;
      const prev = i > 0 ? this.recordedEvents[i - 1]! : null;
      const delta_ms = prev ? entry.timestamp - prev.timestamp : 0;

      if (entry.event.study_id) {
        studyIds.add(entry.event.study_id);
      }

      events.push({ event: entry.event, delta_ms });
    }

    const totalDuration =
      this.recordedEvents.length > 0
        ? this.recordedEvents[this.recordedEvents.length - 1]!.timestamp -
          this.recordedEvents[0]!.timestamp
        : 0;

    return {
      version: 1,
      metadata: {
        recorded_at: new Date().toISOString(),
        total_events: events.length,
        total_duration_ms: Math.round(totalDuration),
        study_ids: Array.from(studyIds),
      },
      events,
    };
  }

  // -----------------------------------------------------------------------
  // Playback
  // -----------------------------------------------------------------------

  /**
   * Start playback. Returns `false` (no-op) if this is a follower tab,
   * since follower tabs cannot disconnect the leader's EventSource.
   */
  startPlayback(recording: SSERecording, speed: PlaybackSpeed = 1): boolean {
    const manager = getManager();

    // Playback only makes sense on the leader tab — follower tabs cannot
    // stop the leader's EventSource and would receive live events on top
    // of replayed ones, breaking deterministic isolation.
    if (!manager.getIsLeaderTab()) {
      return false;
    }

    if (this.state === "recording") {
      this.stopRecording();
    }
    if (this.state === "playing" || this.state === "paused") {
      this.clearPendingTimer();
    }

    this.currentRecording = recording;
    this.playbackIndex = 0;
    this.playbackSpeed = speed;

    // Save the live cursor so we can restore it after playback — replayed
    // events would otherwise pollute lastEventId, causing the reconnect to
    // send a stale/wrong Last-Event-ID header.
    this.savedLastEventId = manager.getLastEventId();

    manager.disconnect();
    manager.clearProcessedEventIds();

    this.setState("playing");
    this.scheduleNextEvent();
    return true;
  }

  pausePlayback(): void {
    if (this.state !== "playing") return;
    this.clearPendingTimer();
    this.setState("paused");
  }

  resumePlayback(speed?: PlaybackSpeed): void {
    if (this.state !== "paused") return;
    if (speed !== undefined) {
      this.playbackSpeed = speed;
    }
    this.setState("playing");
    this.scheduleNextEvent();
  }

  stopPlayback(): void {
    this.clearPendingTimer();
    this.playbackIndex = 0;
    this.setState("idle");
    this.reconnectLiveSSE();
  }

  changePlaybackSpeed(speed: PlaybackSpeed): void {
    this.playbackSpeed = speed;
    // If currently playing, restart the timer chain with the new speed
    if (this.state === "playing") {
      this.clearPendingTimer();
      this.scheduleNextEvent();
    }
  }

  private scheduleNextEvent(): void {
    if (!this.currentRecording) return;
    if (this.playbackIndex >= this.currentRecording.events.length) {
      this.finishPlayback();
      return;
    }

    const entry = this.currentRecording.events[this.playbackIndex]!;
    const delay =
      this.playbackSpeed === "max" ? 0 : entry.delta_ms / this.playbackSpeed;

    this.currentTimer = window.setTimeout(() => {
      this.currentTimer = null;
      const manager = getManager();
      manager.processEvent(entry.event);
      this.playbackIndex++;
      this.opts.onPlaybackProgress?.(
        this.playbackIndex,
        this.currentRecording!.events.length,
      );
      this.opts.onEventPlayed?.(entry.event);
      this.scheduleNextEvent();
    }, delay);
  }

  private finishPlayback(): void {
    this.clearPendingTimer();
    this.setState("idle");
    this.reconnectLiveSSE();
  }

  /**
   * Restore the pre-playback lastEventId cursor, flush the dedup set, and
   * reconnect live SSE. Without the dedup clear, replayed event IDs would
   * linger and cause incoming live events with the same IDs to be silently
   * dropped after reconnect.
   */
  private reconnectLiveSSE(): void {
    const manager = getManager();
    manager.setLastEventId(this.savedLastEventId);
    this.savedLastEventId = null;
    manager.clearProcessedEventIds();
    manager.connect();
  }

  private clearPendingTimer(): void {
    if (this.currentTimer !== null) {
      window.clearTimeout(this.currentTimer);
      this.currentTimer = null;
    }
  }

  // -----------------------------------------------------------------------
  // Import / Export
  // -----------------------------------------------------------------------

  static exportToFile(recording: SSERecording): void {
    const json = JSON.stringify(recording, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    const dateStr = new Date()
      .toISOString()
      .replace(/[:.]/g, "-")
      .slice(0, 19);
    a.download = `sse-recording-${dateStr}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static async importFromFile(file: File): Promise<SSERecording> {
    const text = await file.text();
    const parsed = JSON.parse(text) as unknown;

    // Validate structure
    if (!parsed || typeof parsed !== "object") {
      throw new Error("Invalid recording file: not a JSON object");
    }

    const rec = parsed as Record<string, unknown>;

    if (rec.version !== 1) {
      // Future: add version migration here, e.g. if (rec.version === 1) migrateV1toV2(rec);
      throw new Error(
        `Unsupported recording version: ${String(rec.version)} (expected 1)`,
      );
    }

    if (!rec.metadata || typeof rec.metadata !== "object") {
      throw new Error("Invalid recording file: missing metadata");
    }

    if (!Array.isArray(rec.events)) {
      throw new Error("Invalid recording file: events is not an array");
    }

    // Validate each event has the required shape
    for (let i = 0; i < rec.events.length; i++) {
      const entry = rec.events[i] as Record<string, unknown>;
      if (!entry.event || typeof entry.event !== "object") {
        throw new Error(`Invalid event at index ${i}: missing event object`);
      }
      if (typeof entry.delta_ms !== "number") {
        throw new Error(`Invalid event at index ${i}: delta_ms is not a number`);
      }
    }

    return parsed as SSERecording;
  }

  // -----------------------------------------------------------------------
  // Lifecycle
  // -----------------------------------------------------------------------

  private setState(newState: RecorderState): void {
    this.state = newState;
    this.opts.onStateChange?.(newState);
  }

  destroy(): void {
    const wasMidPlayback =
      this.state === "playing" || this.state === "paused";

    if (this.recordingListener) {
      try {
        const manager = getManager();
        manager.removeListener(this.recordingListener);
      } catch {
        // Manager may already be destroyed
      }
      this.recordingListener = null;
    }
    this.clearPendingTimer();

    // If destroyed while mid-playback (e.g. panel toggled off), restore the
    // live cursor and reconnect so SSE isn't left permanently disconnected.
    if (wasMidPlayback) {
      try {
        this.reconnectLiveSSE();
      } catch {
        // Manager may already be destroyed
      }
    }

    this.recordedEvents = [];
    this.currentRecording = null;
    this.savedLastEventId = null;
    this.state = "idle";
  }
}
